import Group, { type PouleEntry } from "@/models/Group";
import ApiService from "@/services/api.service";
import { ApiResponse } from "@/types/ApiResponse";
import { RouteParamValue } from "vue-router";
import groupCache from "@/services/groupCache.service";
import signatureColorCache from "@/services/signatureColorCache.service";

export interface OnboardingField {
    key: string;
    label: string;
    type:
        | "text"
        | "email"
        | "tel"
        | "number"
        | "textarea"
        | "select"
        | "checkbox"
        | "radio"
        | "date";
    required: boolean;
    options: string[] | null;
    placeholder: string | null;
    help_text: string | null;
    semantic_key: string | null;
}

export interface OnboardingForm {
    id: number;
    name: string;
    description: string | null;
    fields: OnboardingField[];
}

export interface OnboardingFormResponse {
    has_form: boolean;
    form: OnboardingForm | null;
}

export interface SubGroup {
    id: number;
    name: string;
    avatar?: string | null;
    avatar_url?: string | null;
    member_count: number;
}

function primeSignatureColors(groups: Record<string, unknown>[]): void {
    for (const g of groups) {
        if (g.id && g.signature_color) {
            signatureColorCache.set(
                g.id as number,
                g.signature_color as string,
            );
        }
    }
}

const GroupService = {
    store: async (data: object) => {
        const response = await ApiService.put(`/groups`, data);
        groupCache.markDirty();

        return response;
    },

    find: async (id: string) =>
        ApiService.get("groups/" + id)
            .then((response) => {
                const data = response.data;
                // Pending users get a limited response with status + group info
                if (data.status === "pending" && data.group) {
                    const group = new Group({
                        ...data.group,
                        user_status: "pending",
                    });
                    return group;
                }
                return new Group(data);
            })
            .catch((error) => {
                throw error;
            }),

    openShareLink: async (shareLink: string) => {
        const response = (await ApiService.post(
            "/group/share",
            { shareLink },
            undefined,
        )) as ApiResponse<Group>;
        groupCache.markDirty();

        return response;
    },

    // Join with onboarding data
    joinWithOnboarding: async (
        shareLink: string,
        onboardingData: Record<string, any> = {},
    ) => {
        const response = (await ApiService.post(
            "/group/share/join",
            { shareLink, onboarding_data: onboardingData },
            undefined,
        )) as ApiResponse<Group>;
        groupCache.markDirty();

        return response;
    },

    getPouleInfoByShareLink: (shareLink: string) =>
        ApiService.get(`group/share/name/${shareLink}`).then(
            (response) =>
                response.data as {
                    name: string;
                    isFull: boolean;
                    groupImage?: string;
                    is_team_based?: boolean;
                    poule_entries?: PouleEntry[];
                },
        ),

    // Get onboarding form for a group by share link
    getOnboardingFormByShareLink: (shareLink: string) =>
        ApiService.get(`group/share/${shareLink}/onboarding`).then(
            (response) => response.data as OnboardingFormResponse,
        ),

    // Get onboarding form for a group by ID
    getOnboardingForm: (groupId: string) =>
        ApiService.get(`group/${groupId}/onboarding`).then(
            (response) => response.data as OnboardingFormResponse,
        ),

    // Get user's onboarding data for a group
    getUserOnboardingData: (groupId: string) =>
        ApiService.get(`group/${groupId}/onboarding/me`).then(
            (response) =>
                response.data as {
                    has_data: boolean;
                    data: Record<string, any> | null;
                    form_id: number | null;
                },
        ),

    // Get user status for a group by share link
    getUserStatusByShareLink: (shareLink: string) =>
        ApiService.get(`group/share/${shareLink}/status`).then(
            (response) =>
                response.data as {
                    status: "pending" | "accepted" | "not_member";
                    group?: { id: number; name: string };
                },
        ),

    // Accept a pending user
    acceptUser: (groupId: string, userId: number) =>
        ApiService.post(`groups/${groupId}/users/${userId}/accept`, {}),

    // Deny a pending user
    denyUser: (groupId: string, userId: number) =>
        ApiService.post(`groups/${groupId}/users/${userId}/deny`, {}),

    index: async () => {
        const cachedGroups = groupCache.get();

        const etag = groupCache.getEtag();

        // Only short-circuit to a cheap conditional GET when we have a clean
        // cache AND a stored ETag. Without an ETag we fall through to a full
        // GET instead of blindly serving (possibly stale) cache.
        if (cachedGroups && !groupCache.isDirty() && etag) {
            try {
                await ApiService.setHeader();
                const response = await (
                    await import("axios")
                ).default.get("/groups", {
                    headers: { "If-None-Match": etag },
                    validateStatus: (s: number) => s === 200 || s === 304,
                });

                if (response.status === 304) {
                    return cachedGroups.map((group) => new Group(group));
                }

                const newEtag = response.headers["etag"] ?? null;
                groupCache.set(response.data);
                groupCache.setEtag(newEtag);
                groupCache.markSynced();
                primeSignatureColors(response.data);

                return response.data.map((group: Group) => new Group(group));
            } catch {
                return cachedGroups.map((group) => new Group(group));
            }
        }

        const response = await ApiService.get("/groups");
        const newEtag = response.headers?.["etag"] ?? null;
        groupCache.set(response.data);
        groupCache.setEtag(newEtag);
        groupCache.markSynced();
        primeSignatureColors(response.data);

        return response.data.map((group: Group) => new Group(group));
    },

    getCachedIndex: () => {
        const cachedGroups = groupCache.get();
        if (!cachedGroups) {
            return [];
        }

        return cachedGroups.map((group) => new Group(group));
    },

    loadMatches: async (id: string, page: number, onlyPlayed: boolean) => {
        const params = {
            page,
            played: onlyPlayed ? true : undefined,
            perPage: 7,
        };

        if (onlyPlayed) {
            params.played = true;
        }

        return ApiService.get(`/group/${id}/matches`, params)
            .then((response) => response.data)
            .catch((error) => {
                throw error;
            });
    },

    loadRaces: async (groupId: string, onlyPlayed: boolean) => {
        const params = {
            played: onlyPlayed ? true : undefined,
        };

        if (onlyPlayed) {
            params.played = true;
        }

        return ApiService.get(`/group/${groupId}/races`, params)
            .then((response) => response.data)
            .catch((error) => {
                throw error;
            });
    },

    loadRace: async (groupId: string, raceId: string) => {
        return ApiService.get(`/group/${groupId}/races/${raceId}`)
            .then((response) => response.data)
            .catch((error) => {
                throw error;
            });
    },

    availableTournaments: async (sport: string) => {
        if (sport === "formula1") {
            return [
                {
                    id: "formula1",
                    name: "Formula 1",
                    seasons: [],
                },
            ];
        }
        const response = await ApiService.get(sport + "/seasons");

        return response.data.data;
    },
    leave: async (id: string) => {
        const response = await ApiService.delete(`/groups/${id}/remove/me`);
        groupCache.markDirty();

        return response;
    },
    updatePrediction: async (param: {
        driver_8_id: any;
        driver_9_id: any;
        driver_6_id: any;
        driver_7_id: any;
        driver_5_id: any;
        driver_4_id: any;
        driver_1_id: any;
        driver_2_id: any;
        driver_3_id: any;
        driver_10_id: any;
        group_id: string | RouteParamValue[];
        race_id: any;
        pole_driver_id: any;
        fastest_lap_driver_id: any;
    }) => {
        console.log(param);
        return ApiService.put(
            `/group/${param.group_id}/races/${param.race_id}/predict`,
            param,
        )
            .then((response) => response.data)
            .catch((error) => {
                alert(error);
            });
    },

    getSubGroups: async (groupId: string) => {
        const response = await ApiService.get(`groups/${groupId}/sub-groups`);
        return response.data as SubGroup[];
    },

    joinSubGroup: async (groupId: string, subGroupId: number) => {
        const response = await ApiService.post(
            `groups/${groupId}/sub-groups/${subGroupId}/join`,
            {},
        );
        groupCache.markDirty();
        return response.data;
    },

    invalidateGroupsCache: () => {
        groupCache.markDirty();
    },

    clearGroupsCache: () => {
        groupCache.clear();
    },
};

export default GroupService;
